import { formatDate } from '@angular/common';
import { afterNextRender, Component, computed, inject, Injector, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
  DialogService,
} from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';

import {
  BreederWeekRegistrationField,
  BreederWeekRegistrationFormComponent,
} from './breeder-week-registration-form.component';
import { SharedModule } from '../../../../shared/shared.module';
import Utils from '../../../../shared/utils';
import {
  BreederFlock,
  BreederValueRegistration,
  BreederWeekRegistration,
  InputDefinition,
  InputMethodEnum,
} from '../../../models/flock.model';
import { FlockWeekregistrationKpiService } from '../../../services/flock-weekregistration-kpi.service';
import { WeekRegistrationService } from '../../../services/week-registration-service';
import { upsertBreederWeekRegistrationRequest } from '../../../store/actions/flock.actions';
import { WeekRegistrationVariantConfig } from '../../shared/flock-variant.config';
import { WeekRegistrationDialogShellComponent } from '../../week-registration/week-registration-dialog-shell.component';

export interface BreederWeekRegistrationDialogInputData extends AbstractDialogData {
  weekNr?: number;
  flock: BreederFlock;
  weekRegistrationConfig: WeekRegistrationVariantConfig;
}

interface BreederWeekRegistrationTab {
  name: string;
  showFields: BreederWeekRegistrationField[];
  inputMethod: InputMethodEnum;
}

const BREEDER_FIELDS: BreederWeekRegistrationField[] = [
  'henMortality',
  'roosterMortality',
  'henWeight',
  'roosterWeight',
  'henFeedIntakeInGrams',
  'roosterFeedIntakeInGrams',
  'henWaterIntakeInLiters',
  'roosterWaterIntakeInLiters',
  'eggCount',
  'hatchingEggCount',
  'eggWeightInGrams',
  'fertilityPercentage',
  'hatchPercentage',
  'lightHours',
  'lightIntensity',
];

const FIELD_INPUT_GROUPS: Record<BreederWeekRegistrationField, keyof InputDefinition | null> = {
  henMortality: 'mortality',
  roosterMortality: 'mortality',
  henWeight: 'henWeight',
  roosterWeight: 'henWeight',
  henFeedIntakeInGrams: 'forage',
  roosterFeedIntakeInGrams: 'forage',
  henWaterIntakeInLiters: 'water',
  roosterWaterIntakeInLiters: 'water',
  eggCount: 'eggsAmount',
  hatchingEggCount: 'eggsAmountSecondKind',
  eggWeightInGrams: 'eggWeight',
  fertilityPercentage: null,
  hatchPercentage: null,
  lightHours: null,
  lightIntensity: null,
};

@Component({
  selector: 'app-breeder-week-registration',
  standalone: true,
  imports: [
    WeekRegistrationDialogShellComponent,
    BreederWeekRegistrationFormComponent,
    MatTabsModule,
    SharedModule,
  ],
  templateUrl: './breeder-week-registration.component.html',
  styleUrls: ['./breeder-week-registration.component.scss'],
})
export class BreederWeekRegistrationComponent extends AbstractDialog<{
  weekRegistration: BreederWeekRegistration;
}> {
  private readonly injector = inject(Injector);
  private readonly currentWeekRegistrationState = signal<BreederWeekRegistration>({
    weekNr: 0,
    dayRegistrations: [],
    startDay: null,
    endDay: null,
    registrationData: {},
  });
  private readonly amountOfHensStartOfWeekState = signal<number | undefined>(undefined);
  private readonly amountOfRoostersStartOfWeekState = signal<number | undefined>(undefined);
  private readonly registeredHenMortalityState = signal<number[]>([]);
  private readonly registeredRoosterMortalityState = signal<number[]>([]);
  private readonly selectedTabIndexState = signal(0);
  private readonly tabsState = signal<BreederWeekRegistrationTab[]>([]);
  private readonly formGroupsState = signal<FormGroup[]>([]);

  readonly currentWeekRegistrationValue = this.currentWeekRegistrationState.asReadonly();
  readonly amountOfHensStartOfWeekValue = this.amountOfHensStartOfWeekState.asReadonly();
  readonly amountOfRoostersStartOfWeekValue = this.amountOfRoostersStartOfWeekState.asReadonly();
  readonly selectedTabIndexValue = this.selectedTabIndexState.asReadonly();
  readonly tabsValue = this.tabsState.asReadonly();
  readonly formGroupsValue = this.formGroupsState.asReadonly();
  readonly hasFormGroups = computed(() => this.formGroupsState().length > 0);
  readonly hasRoosters = computed(() => (this.data.flock.arrival.amountOfRoosters ?? 0) > 0);
  readonly inputMethod = computed(
    () =>
      this.weekRegistrationService.getInputMethod(this.data.flock) as InputMethodEnum | 'advanced',
  );
  readonly isWeeklyInputMethod = computed(() => this.inputMethod() === InputMethodEnum.PerWeek);
  readonly hasSeparateWeeklySection = computed(
    () => !this.isWeeklyInputMethod() && this.allWeeklyShowFields().length > 0,
  );
  readonly weeklyFormGroups = computed(() => {
    const formGroup = this.formGroupsState()[0];

    return this.isWeeklyInputMethod() && formGroup ? [formGroup] : [];
  });
  readonly separateWeeklyFormGroup = computed(() =>
    this.hasSeparateWeeklySection() ? this.formGroupsState()[0] : undefined,
  );
  readonly dailyFormGroupOffset = computed(() => (this.hasSeparateWeeklySection() ? 1 : 0));
  readonly dailyFormGroups = computed(() =>
    this.isWeeklyInputMethod() ? [] : this.formGroupsState().slice(this.dailyFormGroupOffset()),
  );
  readonly allWeeklyShowFields = computed(() => this.getWeeklyFields());
  readonly firstDayOfWeek = computed(() =>
    this.kpiService.getDateByHenAgeInDays(
      new Date(this.data.flock.arrival.chickBirthDate),
      this.currentWeekRegistrationState().startDay ?? 0,
    ),
  );
  readonly lastDayOfWeek = computed(() =>
    this.kpiService.getDateByHenAgeInDays(
      new Date(this.data.flock.arrival.chickBirthDate),
      this.currentWeekRegistrationState().endDay ?? 0,
    ),
  );

  weekRegistrations: BreederWeekRegistration[];
  private readonly weekRegistrationConfig: WeekRegistrationVariantConfig;

  readonly data = inject<BreederWeekRegistrationDialogInputData>(MAT_DIALOG_DATA);
  protected readonly mdDialogRef =
    inject<
      MatDialogRef<
        BreederWeekRegistrationComponent,
        AbstractDialogResultData<{ weekRegistration: BreederWeekRegistration }>
      >
    >(MatDialogRef);
  private readonly locale = inject(LocaleService);
  private readonly store = inject(Store);
  private readonly formBuilder = inject(FormBuilder);
  private readonly kpiService = inject(FlockWeekregistrationKpiService);
  private readonly weekRegistrationService = inject(WeekRegistrationService);
  private readonly dialogService = inject(DialogService);

  constructor() {
    super();
    this.weekRegistrationConfig = this.data.weekRegistrationConfig;
    this.weekRegistrations = this.data.flock.weekRegistrations
      .slice()
      .sort((w1, w2) => (w1.weekNr > w2.weekNr ? 1 : -1));
    this.initialize();
  }

  private initialize(): void {
    this.tabsState.set([]);
    this.formGroupsState.set([]);
    this.registeredHenMortalityState.set([]);
    this.registeredRoosterMortalityState.set([]);
    this.selectedTabIndexState.set(0);

    const givenWeek = this.weekRegistrations.find((week) => week.weekNr === this.data.weekNr);
    let currentWeekRegistration = this.weekRegistrationService.getCurrentWeek(
      givenWeek,
      this.weekRegistrations,
      this.data.flock,
      this.weekRegistrationConfig.productionPeriod,
    );
    this.currentWeekRegistrationState.set(currentWeekRegistration);

    const previousWeek = this.weekRegistrations.find(
      (week) => week.weekNr === currentWeekRegistration.weekNr - 1,
    );
    this.amountOfHensStartOfWeekState.set(
      previousWeek === undefined
        ? this.data.flock.arrival.amountOfHens
        : this.getAmountsForWeekNr('henMortality', previousWeek.weekNr),
    );
    this.amountOfRoostersStartOfWeekState.set(
      previousWeek === undefined
        ? (this.data.flock.arrival.amountOfRoosters ?? 0)
        : this.getAmountsForWeekNr('roosterMortality', previousWeek.weekNr),
    );

    this.data.title = `${this.data.flock.name} - ${this.locale.translate(
      'week-registration.week',
    )} ${currentWeekRegistration.weekNr} ${this.locale.translate(
      'week-registration.registration',
    )}`;

    if (this.isWeeklyInputMethod()) {
      this.registeredHenMortalityState.set([0]);
      this.registeredRoosterMortalityState.set([0]);
      this.createFormGroup(
        currentWeekRegistration.registrationData,
        this.getWeeklyFields(),
        this.amountOfHensStartOfWeekState() ?? 0,
        this.amountOfRoostersStartOfWeekState() ?? 0,
        false,
      );
      return;
    }

    currentWeekRegistration = this.addMissingDayRegistrations(currentWeekRegistration);
    this.currentWeekRegistrationState.set(currentWeekRegistration);
    this.createTabs();

    if (this.hasSeparateWeeklySection()) {
      this.registeredHenMortalityState.set([...this.registeredHenMortalityState(), 0]);
      this.registeredRoosterMortalityState.set([...this.registeredRoosterMortalityState(), 0]);
      this.createFormGroup(
        currentWeekRegistration.registrationData,
        this.getWeeklyFields(),
        this.amountOfHensStartOfWeekState() ?? 0,
        this.amountOfRoostersStartOfWeekState() ?? 0,
        false,
      );
    }

    let dayRegistrationIndex = 0;
    this.tabsState().forEach((tab, index) => {
      const dayRegistration = currentWeekRegistration.dayRegistrations[dayRegistrationIndex];
      this.registeredHenMortalityState.set([
        ...this.registeredHenMortalityState(),
        dayRegistration.registrationData.henMortality ?? 0,
      ]);
      this.registeredRoosterMortalityState.set([
        ...this.registeredRoosterMortalityState(),
        dayRegistration.registrationData.roosterMortality ?? 0,
      ]);
      this.createFormGroup(
        dayRegistration.registrationData,
        tab.showFields,
        this.calculateAmountAtStartOfDay(
          this.amountOfHensStartOfWeekState(),
          this.registeredHenMortalityState(),
          index,
          InputMethodEnum.PerDay,
        ),
        this.calculateAmountAtStartOfDay(
          this.amountOfRoostersStartOfWeekState(),
          this.registeredRoosterMortalityState(),
          index,
          InputMethodEnum.PerDay,
        ),
      );
      dayRegistrationIndex++;
    });

    afterNextRender(() => this.scrollDialogContentToTop(), { injector: this.injector });
  }

  private scrollDialogContentToTop(): void {
    const dialogContent = document.querySelector<HTMLElement>('.mat-mdc-dialog-content');
    if (!dialogContent) {
      return;
    }

    dialogContent.scrollTop = 0;
    dialogContent.scrollTo(0, 0);
  }

  private addMissingDayRegistrations(
    weekRegistration: BreederWeekRegistration,
  ): BreederWeekRegistration {
    let dayRegistrations = weekRegistration.dayRegistrations;
    for (let i = 0; i < weekRegistration.endDay + 1 - weekRegistration.startDay; i++) {
      if (!dayRegistrations.some((dayReg) => dayReg.day === weekRegistration.startDay + i)) {
        dayRegistrations = [
          ...dayRegistrations,
          {
            day: weekRegistration.startDay + i,
            registrationData: {},
          },
        ];
      }
    }

    return { ...weekRegistration, dayRegistrations };
  }

  private createTabs(): void {
    const tabs: BreederWeekRegistrationTab[] = [];
    const dailyFields = this.getDailyFields();

    this.currentWeekRegistrationState().dayRegistrations.forEach((day) => {
      const date = Utils.toUtcDate(new Date(this.data.flock.arrival.chickBirthDate));
      date.setDate(date.getDate() + day.day);

      if (this.isAfterDeparture(day.day)) {
        return;
      }

      tabs.push({
        name: formatDate(date, 'd MMM yy', this.locale.getLocale()),
        showFields: dailyFields,
        inputMethod: InputMethodEnum.PerDay,
      });
    });

    this.tabsState.set(tabs);
  }

  private isAfterDeparture(day: number): boolean {
    if (!this.data.flock.departure) {
      return false;
    }

    const departureDate = new Date(this.data.flock.departure.departureDate);
    const departureDay = Math.round(
      Math.abs(
        departureDate.getTime() - new Date(this.data.flock.arrival.chickBirthDate).getTime(),
      ) /
        (1000 * 3600 * 24),
    );

    return day >= departureDay;
  }

  private getDailyFields(): BreederWeekRegistrationField[] {
    return this.filterRoosterFields(
      BREEDER_FIELDS.filter((field) => {
        const group = FIELD_INPUT_GROUPS[field];
        return group !== null && this.data.flock.inputDefinition[group] === InputMethodEnum.PerDay;
      }),
    );
  }

  private getWeeklyFields(): BreederWeekRegistrationField[] {
    return this.filterRoosterFields(
      BREEDER_FIELDS.filter((field) => {
        const group = FIELD_INPUT_GROUPS[field];
        return (
          this.isWeeklyInputMethod() ||
          group === null ||
          this.data.flock.inputDefinition[group] === InputMethodEnum.PerWeek
        );
      }),
    );
  }

  private filterRoosterFields(
    fields: BreederWeekRegistrationField[],
  ): BreederWeekRegistrationField[] {
    if (this.hasRoosters()) {
      return fields;
    }

    return fields.filter((field) => !field.toLowerCase().includes('rooster'));
  }

  private createFormGroup(
    data: BreederValueRegistration,
    showFields: BreederWeekRegistrationField[],
    amountOfHens: number,
    amountOfRoosters: number,
    applyMortalityMax = true,
  ): void {
    const optionalControlState = <T>(value: T | undefined) => ({
      value: value ?? null,
      disabled: false,
    });
    const validators = (field: BreederWeekRegistrationField) =>
      showFields.includes(field)
        ? this.getValidators(field, amountOfHens, amountOfRoosters, applyMortalityMax)
        : [];

    const formGroup = this.formBuilder.group(
      {
        henMortality: [optionalControlState(data?.henMortality), validators('henMortality')],
        roosterMortality: [
          optionalControlState(data?.roosterMortality),
          validators('roosterMortality'),
        ],
        henWeight: [optionalControlState(data?.henWeight), validators('henWeight')],
        roosterWeight: [optionalControlState(data?.roosterWeight), validators('roosterWeight')],
        henFeedIntakeInGrams: [
          optionalControlState(data?.henFeedIntakeInGrams),
          validators('henFeedIntakeInGrams'),
        ],
        roosterFeedIntakeInGrams: [
          optionalControlState(data?.roosterFeedIntakeInGrams),
          validators('roosterFeedIntakeInGrams'),
        ],
        henWaterIntakeInLiters: [
          optionalControlState(data?.henWaterIntakeInLiters),
          validators('henWaterIntakeInLiters'),
        ],
        roosterWaterIntakeInLiters: [
          optionalControlState(data?.roosterWaterIntakeInLiters),
          validators('roosterWaterIntakeInLiters'),
        ],
        eggCount: [optionalControlState(data?.eggCount), validators('eggCount')],
        hatchingEggCount: [
          optionalControlState(data?.hatchingEggCount),
          validators('hatchingEggCount'),
        ],
        eggWeightInGrams: [
          optionalControlState(data?.eggWeightInGrams),
          validators('eggWeightInGrams'),
        ],
        fertilityPercentage: [
          optionalControlState(data?.fertilityPercentage),
          validators('fertilityPercentage'),
        ],
        hatchPercentage: [
          optionalControlState(data?.hatchPercentage),
          validators('hatchPercentage'),
        ],
        lightHours: [optionalControlState(data?.lightHours), validators('lightHours')],
        lightIntensity: [optionalControlState(data?.lightIntensity), validators('lightIntensity')],
      },
      {
        validators: [this.fertilityHatchPercentageValidator()],
      },
    );

    this.formGroupsState.set([...this.formGroupsState(), formGroup]);
  }

  private fertilityHatchPercentageValidator(): (
    control: AbstractControl,
  ) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      const fertilityPercentage = control.get('fertilityPercentage')?.value;
      const hatchPercentage = control.get('hatchPercentage')?.value;

      if (
        fertilityPercentage !== null &&
        fertilityPercentage !== undefined &&
        hatchPercentage !== null &&
        hatchPercentage !== undefined &&
        fertilityPercentage < hatchPercentage
      ) {
        return { fertilityLessThanHatch: true };
      }
      return null;
    };
  }

  private getValidators(
    field: BreederWeekRegistrationField,
    amountOfHens: number,
    amountOfRoosters: number,
    applyMortalityMax = true,
  ) {
    if (field === 'henMortality') {
      return applyMortalityMax
        ? [Validators.min(0), Validators.max(amountOfHens), Validators.pattern(/^\d+$/)]
        : [Validators.min(0), Validators.pattern(/^\d+$/)];
    }

    if (field === 'roosterMortality') {
      return applyMortalityMax
        ? [Validators.min(0), Validators.max(amountOfRoosters), Validators.pattern(/^\d+$/)]
        : [Validators.min(0), Validators.pattern(/^\d+$/)];
    }

    if (field === 'fertilityPercentage' || field === 'hatchPercentage') {
      return [Validators.min(0), Validators.max(100)];
    }

    if (field === 'hatchingEggCount') {
      return [Validators.min(0), Validators.pattern(/^\d+$/)];
    }

    if (field === 'lightHours') {
      return [Validators.min(0), Validators.max(24)];
    }

    return [Validators.min(0)];
  }

  private getAmountsForWeekNr(
    mortalityField: 'henMortality' | 'roosterMortality',
    weekNr: number,
  ): number {
    let startAmount =
      mortalityField === 'henMortality'
        ? this.data.flock.arrival.amountOfHens
        : (this.data.flock.arrival.amountOfRoosters ?? 0);

    this.weekRegistrations.forEach((week) => {
      if (week.weekNr <= weekNr) {
        startAmount -= this.getWeekMortality(week, mortalityField);
      }
    });

    return startAmount;
  }

  private getWeekMortality(
    week: BreederWeekRegistration,
    mortalityField: 'henMortality' | 'roosterMortality',
  ): number {
    return week.registrationData[mortalityField]
      ? week.registrationData[mortalityField]
      : week.dayRegistrations.reduce(
          (currentValue, dayRegistration) =>
            currentValue + (dayRegistration.registrationData[mortalityField] ?? 0),
          0,
        );
  }

  private calculateAmountAtStartOfDay(
    amountStartOfWeek: number | undefined,
    registeredMortality: number[],
    index: number,
    inputMethod: InputMethodEnum,
  ): number {
    if (amountStartOfWeek === undefined) {
      return 0;
    }

    if (inputMethod === InputMethodEnum.PerWeek) {
      return amountStartOfWeek;
    }

    let mortalityStartOfDay = 0;
    for (let i = 0; i < index; i++) {
      mortalityStartOfDay += Number(registeredMortality[i]) || 0;
    }

    return Math.max(0, amountStartOfWeek - mortalityStartOfDay);
  }

  private getFormGroupData(index: number): BreederValueRegistration {
    return this.formGroupsState()[index].value as BreederValueRegistration;
  }

  private getDataFromForms(): BreederWeekRegistration {
    const currentWeekRegistration = this.currentWeekRegistrationState();
    const newWeekRegistration: BreederWeekRegistration = {
      dayRegistrations: [],
      endDay: currentWeekRegistration.endDay,
      startDay: currentWeekRegistration.startDay,
      weekNr: currentWeekRegistration.weekNr,
      registrationData: {},
    };
    let dayIndex = 0;

    if (this.isWeeklyInputMethod()) {
      newWeekRegistration.registrationData = this.clearRoosterFields(this.getFormGroupData(0));
      return newWeekRegistration;
    }

    const dailyFormGroupOffset = this.dailyFormGroupOffset();
    if (this.hasSeparateWeeklySection()) {
      newWeekRegistration.registrationData = this.clearRoosterFields(this.getFormGroupData(0));
    }

    this.tabsState().forEach((_tab, formIndex) => {
      newWeekRegistration.dayRegistrations.push({
        day: currentWeekRegistration.dayRegistrations[dayIndex].day,
        registrationData: this.clearRoosterFields(
          this.getFormGroupData(formIndex + dailyFormGroupOffset),
        ),
      });
      dayIndex++;
    });

    return {
      ...newWeekRegistration,
      endDay:
        newWeekRegistration.dayRegistrations.length > 0
          ? newWeekRegistration.dayRegistrations.reduce(
              (maxDay, { day }) => Math.max(maxDay, day),
              newWeekRegistration.dayRegistrations[0].day,
            )
          : newWeekRegistration.endDay,
    };
  }

  private clearRoosterFields(registrationData: BreederValueRegistration): BreederValueRegistration {
    if (this.hasRoosters()) {
      return { ...registrationData };
    }

    return {
      ...registrationData,
      roosterMortality: null,
      roosterWeight: null,
      roosterFeedIntakeInGrams: null,
      roosterWaterIntakeInLiters: null,
    };
  }

  save(): void {
    this.handleSave(true);
  }

  saveAndNextWeek(): void {
    this.handleSave(false);
  }

  private handleSave(applyConfirm: boolean): void {
    const formGroups = this.formGroupsState();

    if (!formGroups.every((formGroup) => formGroup.valid)) {
      const indexOfFailedTab = formGroups.findIndex((formGroup) => !formGroup.valid);
      const dailyFormGroupOffset = this.dailyFormGroupOffset();
      if (indexOfFailedTab >= dailyFormGroupOffset) {
        this.selectedTabIndexState.set(indexOfFailedTab - dailyFormGroupOffset);
      }
      formGroups.forEach((formGroup) => formGroup.markAllAsTouched());
      return;
    }

    const finalWeekRegistration = { ...this.getDataFromForms() };

    if (
      this.totalMortality(finalWeekRegistration, 'henMortality') >
      this.amountOfHensStartOfWeekState()
    ) {
      this.showMortalityWarning();
      return;
    }

    if (
      this.totalMortality(finalWeekRegistration, 'roosterMortality') >
      this.amountOfRoostersStartOfWeekState()
    ) {
      this.showMortalityWarning();
      return;
    }

    this.upsertLocalWeekRegistration(finalWeekRegistration);
    this.store.dispatch(
      upsertBreederWeekRegistrationRequest({
        flockId: this.data.flock.id,
        weekRegistration: finalWeekRegistration,
      }),
    );

    if (applyConfirm) {
      this.confirm();
      return;
    }

    afterNextRender(() => this.initialize(), { injector: this.injector });
  }

  private totalMortality(
    weekRegistration: BreederWeekRegistration,
    mortalityField: 'henMortality' | 'roosterMortality',
  ): number {
    const weeklyMortality = weekRegistration.registrationData[mortalityField];
    if (weeklyMortality !== null && weeklyMortality !== undefined) {
      return Number(weeklyMortality) || 0;
    }

    return weekRegistration.dayRegistrations.reduce(
      (sum, dayRegistration) =>
        sum + (Number(dayRegistration.registrationData[mortalityField]) || 0),
      0,
    );
  }

  private showMortalityWarning(): void {
    this.dialogService.confirmWarning({
      title: this.locale.translate('validation-error.title'),
      text: this.locale.translate('week-registration.too-much-mortality'),
      disableClose: true,
      pushHistoryState: false,
    });
  }

  private upsertLocalWeekRegistration(weekRegistration: BreederWeekRegistration): void {
    const existingIndex = this.weekRegistrations.findIndex(
      (week) => week.weekNr === weekRegistration.weekNr,
    );

    this.weekRegistrations =
      existingIndex === -1
        ? [...this.weekRegistrations, weekRegistration]
        : this.weekRegistrations.map((week) =>
            week.weekNr === weekRegistration.weekNr ? weekRegistration : week,
          );
    this.data.flock = {
      ...this.data.flock,
      weekRegistrations: this.weekRegistrations,
    };
  }

  onSelectedTabIndexChange(index: number): void {
    this.selectedTabIndexState.set(index);
  }

  mortalityForDayChanged(event: {
    index: number;
    henMortality: number;
    roosterMortality: number;
  }): void {
    if (event.index < 0) {
      return;
    }

    const updatedHenMortality = this.dailyFormGroups().map(
      (formGroup) => Number(formGroup.controls.henMortality.value) || 0,
    );
    const updatedRoosterMortality = this.dailyFormGroups().map(
      (formGroup) => Number(formGroup.controls.roosterMortality.value) || 0,
    );
    updatedHenMortality[event.index] = Number(event.henMortality) || 0;
    updatedRoosterMortality[event.index] = Number(event.roosterMortality) || 0;
    this.registeredHenMortalityState.set(updatedHenMortality);
    this.registeredRoosterMortalityState.set(updatedRoosterMortality);

    const dailyFormGroupOffset = this.dailyFormGroupOffset();
    this.tabsState().forEach((_tab, index) => {
      if (index <= event.index) {
        return;
      }

      this.updateMortalityValidators(
        this.formGroupsState()[index + dailyFormGroupOffset],
        this.calculateAmountAtStartOfDay(
          this.amountOfHensStartOfWeekState(),
          updatedHenMortality,
          index,
          InputMethodEnum.PerDay,
        ),
        this.calculateAmountAtStartOfDay(
          this.amountOfRoostersStartOfWeekState(),
          updatedRoosterMortality,
          index,
          InputMethodEnum.PerDay,
        ),
      );
    });
  }

  private updateMortalityValidators(
    formGroup: FormGroup,
    amountOfHensStartOfDay: number,
    amountOfRoostersStartOfDay: number,
  ): void {
    formGroup.controls.henMortality.setValidators([
      Validators.min(0),
      Validators.max(amountOfHensStartOfDay),
      Validators.pattern(/^\d+$/),
    ]);
    formGroup.controls.roosterMortality.setValidators([
      Validators.min(0),
      Validators.max(amountOfRoostersStartOfDay),
      Validators.pattern(/^\d+$/),
    ]);
    formGroup.markAllAsTouched();
    formGroup.controls.henMortality.updateValueAndValidity();
    formGroup.controls.roosterMortality.updateValueAndValidity();
  }
}
